import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { selectCrsToken, selectCrsUser, selectDomain } from '../../slices/metadata/selectors'
import { RootState } from '../../createStore'
import type {
  ClientType,
  ClientTypeResponse,
  Product,
  ModuleCategory,
  UpdateModuleConfigurationPayload,
  DetailedModule,
  VersionHistory,
  VersionDataPayload,
  Module,
  ModuleCSVContent,
  ModuleIds,
} from './types'
import { TAG } from './constants'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { getEnvVar } from '@client-hub/shared/utils/env'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { EnvironmentVariable } from '@client-hub/shared/constants/env'

export const ccsApi = createApi({
  reducerPath: 'ccsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${getEnvVar(EnvironmentVariable.CCS_API_URL)}/api/`,
    prepareHeaders: (headers, { getState }) => {
      const rootState = getState() as RootState
      const token = selectCrsToken(rootState)
      const user: any = selectCrsUser(rootState)
      if (token) {
        headers.set('Authorization', `Token ${token}`)
      }
      headers.set('env', getEnvVar(EnvironmentVariable.ENVIRONMENT))
      if ('client_slug' in user) {
        headers.set('client-slug', user.client_slug || 'bwell')
      }
      let domain = selectDomain(rootState)
      // required if running on local
      if (!domain?.includes('icanbwell')) {
        domain = 'bwell.hub.dev.icanbwell.com'
      }
      headers.set('domain', domain)
      return headers
    },
  }),
  tagTypes: [...Object.values(TAG)],
  endpoints: (build) => ({
    clientType: build.query<ClientTypeResponse, void>({
      query: () => '/clients/client-type/',
      providesTags: [TAG.CLIENT_TYPE],
    }),
    addClientType: build.mutation<ClientType, ClientType>({
      query: (body) => ({ url: '/clients/client-type/', method: 'POST', body }),
      invalidatesTags: [TAG.CLIENT_TYPE],
    }),
    clientTypeUpdate: build.mutation<ClientType, ClientType>({
      query: (body) => ({ url: `/clients/client-type/${body.id}/`, method: 'PUT', body }),
      invalidatesTags: [TAG.CLIENT_TYPE],
    }),
    clientTypeDelete: build.mutation<ClientType, number>({
      query: (id) => ({ url: `/clients/client-type/${id}`, method: 'DELETE' }),
      invalidatesTags: [TAG.CLIENT_TYPE],
    }),
    product: build.query<Product[], void>({
      query: () => '/product-configuration/product-categories/',
      providesTags: [TAG.PRODUCT],
    }),
    modules: build.query<Module[], void>({
      query: () => '/product-configuration/module/',
      providesTags: [TAG.MODULE],
    }),
    module: build.query<Module, number>({
      query: (id) => `/product-configuration/module/${id}/`,
      providesTags: [TAG.MODULE],
    }),
    updateModule: build.mutation<Module, Pick<Module, 'id'> & Partial<Module>>({
      query: (body) => ({ url: `/product-configuration/module/${body.id}/`, method: 'PUT', body }),
      invalidatesTags: [TAG.MODULE],
    }),
    addModule: build.mutation<Module, Module>({
      query: (body) => ({ url: `/product-configuration/module/`, method: 'POST', body }),
      invalidatesTags: [TAG.MODULE],
    }),
    moduleCategory: build.query<ModuleCategory[], void>({
      query: () => '/product-configuration/module/categories',
      providesTags: [TAG.MODULE],
    }),
    genericModules: build.query<DetailedModule[], void>({
      query: () => '/product-configuration/generic-modules/',
      providesTags: [TAG.GENERIC_MODULE, TAG.MODULE],
    }),
    genericModule: build.query<DetailedModule, string>({
      query: (module) => `/product-configuration/generic-modules/${module}/`,
      providesTags: [TAG.GENERIC_MODULE],
    }),
    genericModuleConfiguration: build.query<DetailedModule, string>({
      query: (module) => `/product-configuration/generic-configurations/${module}/`,
      providesTags: [TAG.GENERIC_MODULE_CONFIG],
    }),
    createGenericModuleConfiguration: build.mutation<DetailedModule, UpdateModuleConfigurationPayload>({
      query: (body) => ({
        url: `/product-configuration/generic-configurations/${body.module}/`,
        method: 'POST',
        body: body.data,
      }),
      invalidatesTags: [TAG.GENERIC_MODULE_CONFIG],
    }),
    updateGenericModuleCongifuration: build.mutation<DetailedModule, UpdateModuleConfigurationPayload>({
      query: (body) => ({
        url: `/product-configuration/generic-configurations/${body.module}/`,
        method: 'PUT',
        body: body.data,
      }),
      invalidatesTags: [TAG.GENERIC_MODULE_CONFIG],
    }),
    getModuleVersionHistory: build.query<VersionHistory[], string>({
      query: (module) => `/product-configuration/module/${module}/history/`,
      providesTags: [TAG.MODULE_HISTORY_VERSIONS],
    }),
    getModuleVersionData: build.query<Module, VersionDataPayload>({
      query: (body) => `/product-configuration/module/${body.module}/history/${body.id}`,
      providesTags: [TAG.MODULE_HISTORY_VERSIONS],
    }),
    getGenericModuleVersionHistory: build.query<VersionHistory[], string>({
      query: (module) => `/product-configuration/generic-configurations/${module}/history/`,
      providesTags: [TAG.MODULE_HISTORY_VERSIONS],
    }),
    getGenericModuleVersionData: build.query<DetailedModule, VersionDataPayload>({
      query: (body) => `/product-configuration/generic-configurations/${body.module}/history/${body.id}`,
      providesTags: [TAG.MODULE_HISTORY_VERSIONS],
    }),
    downloadModule: build.mutation<ModuleCSVContent, ModuleIds>({
      query: (body) => ({
        url: `/product-configuration/module/export`,
        method: 'POST',
        body: body,
      }),
    }),
    importModule: build.mutation<Module[], File>({
      query: (file) => {
        const body = new FormData()
        body.append('file', file)
        return { url: `/product-configuration/module/import`, method: 'POST', body }
      },
      invalidatesTags: [TAG.MODULE],
    }),
  }),
})

export const {
  useProductQuery,
  useModulesQuery,
  useModuleQuery,
  useUpdateModuleMutation,
  useAddModuleMutation,
  useModuleCategoryQuery,
  useGenericModuleQuery,
  useGenericModulesQuery,
  useGenericModuleConfigurationQuery,
  useCreateGenericModuleConfigurationMutation,
  useUpdateGenericModuleCongifurationMutation,
  useLazyGetGenericModuleVersionHistoryQuery,
  useLazyGetGenericModuleVersionDataQuery,
  useLazyGetModuleVersionHistoryQuery,
  useLazyGetModuleVersionDataQuery,
  useDownloadModuleMutation,
  useImportModuleMutation,
} = ccsApi
